import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { canWrite, canChangeStatus, WRITE_PERMISSION_DENIED } from '@/services/permissions';
import { recordAudit } from '@/services/audit';
import { logInventoryChange } from '@/services/inventory';

export interface Consignment {
  id: string;
  distributorName: string;
  status: string;
  itemCount: number;
  notes: string;
  createdAt: string;
}

export interface ConsignmentsResult {
  data: Consignment[];
}

// Consulta las consignaciones reales desnormalizando distribuidor e items.
// Sin datos devuelve un array vacío (honesto) en vez de inventar mocks.
export async function fetchConsignments(): Promise<ConsignmentsResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const [consignmentsRes, distributorsRes, itemsRes] = await Promise.all([
      supabase.from('consignments').select('*'),
      supabase.from('distributors').select('id, business_name'),
      supabase.from('consignment_items').select('consignment_id, quantity_delivered'),
    ]);

    if (consignmentsRes.error) throw new Error(consignmentsRes.error.message);
    if (distributorsRes.error) throw new Error(distributorsRes.error.message);
    if (itemsRes.error) throw new Error(itemsRes.error.message);

    const distributorMap = new Map(
      ((distributorsRes.data ?? []) as Array<Record<string, any>>).map((d) => [
        d.id,
        d.business_name,
      ]),
    );

    const countByConsignment = new Map<string, number>();
    ((itemsRes.data ?? []) as Array<Record<string, any>>).forEach((it) => {
      countByConsignment.set(
        it.consignment_id,
        (countByConsignment.get(it.consignment_id) ?? 0) + Number(it.quantity_delivered),
      );
    });

    const data: Consignment[] = ((consignmentsRes.data ?? []) as Array<Record<string, any>>)
      .map((c) => ({
        id: c.id,
        distributorName: c.distributor_id ? distributorMap.get(c.distributor_id) ?? '' : '',
        status: c.status ?? '',
        itemCount: countByConsignment.get(c.id) ?? 0,
        notes: c.notes ?? '',
        createdAt: c.created_at ?? '',
      }))
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));

    return { data };
  } catch (err) {
    console.error('[consignments] connection failed:', err);
    throw err;
  }
}

export interface ConsignmentItemInput {
  variantId: string;
  quantity: number;
  unitPrice: number;
  unitCost: number;
}

export interface ConsignmentInput {
  distributorId: string;
  status: string;
  notes: string;
  items: ConsignmentItemInput[];
}

export async function saveConsignment(
  input: ConsignmentInput,
): Promise<{ data: Consignment | null; error: string | null }> {
  if (!(await canWrite('consignments'))) {
    return { data: null, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { data: null, error: 'Supabase no está configurado.' };
  }

  const payload = {
    distributor_id: input.distributorId || null,
    status: input.status || 'active',
    notes: input.notes.trim() || null,
  };

  try {
    const { data, error } = await supabase.from('consignments').insert(payload).select('*');
    if (error) return { data: null, error: error.message };
    const row = data?.[0] as Record<string, any> | undefined;
    if (!row) return { data: null, error: 'No se pudo leer la consignación guardada.' };

    // Líneas de producto (variante + cantidad entregada + precio unitario).
    const items = input.items.map((it) => ({
      consignment_id: row.id,
      variant_id: it.variantId,
      quantity_delivered: it.quantity,
      quantity_sold: 0,
      quantity_returned: 0,
      unit_price: it.unitPrice,
    }));
    if (items.length > 0) {
      const { error: itemsError } = await supabase.from('consignment_items').insert(items);
      if (itemsError) {
        console.warn('[consignments] consignment_items insert failed:', itemsError.message);
      }
    }

    // Actualiza inventario: consigned += cantidad, available -= cantidad.
    for (const it of input.items) {
      const { data: inv } = await supabase
        .from('inventory')
        .select('available, reserved, consigned')
        .eq('variant_id', it.variantId)
        .maybeSingle();
      const available = Number(inv?.available ?? 0);
      const reserved = Number(inv?.reserved ?? 0);
      const consigned = Number(inv?.consigned ?? 0);
      const nextAvailable = Math.max(0, available - it.quantity);
      const nextConsigned = consigned + it.quantity;

      await supabase
        .from('inventory')
        .update({ available: nextAvailable, consigned: nextConsigned })
        .eq('variant_id', it.variantId);

      await logInventoryChange(it.variantId, [
        { fieldName: 'available', oldValue: available, newValue: nextAvailable },
        { fieldName: 'consigned', oldValue: consigned, newValue: nextConsigned },
      ]);

      await supabase.from('inventory_movements').insert({
        variant_id: it.variantId,
        movement_type: 'CONSIGNMENT_OUT',
        quantity: it.quantity,
        prev_available: available,
        new_available: nextAvailable,
        prev_reserved: reserved,
        new_reserved: reserved,
        prev_consigned: consigned,
        new_consigned: nextConsigned,
        unit_cost: it.unitCost,
        reason: 'Consignación a distribuidor',
      });
    }

    recordAudit({
      entityType: 'consignments',
      entityId: row.id,
      action: 'CREATE',
      newValue: String(input.items.length),
    });

    // Recalcula el estado de crédito del distribuidor según su saldo abierto.
    if (input.distributorId) {
      await recalculateDistributorCreditStatus(input.distributorId);
    }

    const totalDelivered = input.items.reduce((sum, it) => sum + it.quantity, 0);

    return {
      data: {
        id: row.id,
        distributorName: '',
        status: row.status ?? '',
        itemCount: totalDelivered,
        notes: row.notes ?? '',
        createdAt: row.created_at ?? '',
      },
      error: null,
    };
  } catch (err) {
    console.warn('[consignments] save failed:', err);
    return { data: null, error: 'Error de conexión al guardar la consignación.' };
  }
}

// --- Detalle de consignación: items, abonos y estado de pago ---

export interface ConsignmentItemDetail {
  id: string;
  variantId: string;
  productName: string;
  sizeLabel: string;
  quantityDelivered: number;
  quantityReturned: number;
  quantitySold: number;
  unitPrice: number;
}

export interface ConsignmentPayment {
  id: string;
  consignmentId: string;
  amount: number;
  paidAt: string;
  notes: string;
  createdAt: string;
}

export interface ConsignmentDetail {
  id: string;
  distributorName: string;
  status: string;
  notes: string;
  createdAt: string;
  items: ConsignmentItemDetail[];
  payments: ConsignmentPayment[];
}

export async function fetchConsignmentDetail(id: string): Promise<ConsignmentDetail | null> {
  if (!isSupabaseConfigured || !supabase) return null;

  const [consRes, itemsRes, payRes, distributorsRes, variantsRes, productsRes, sizesRes] =
    await Promise.all([
      supabase.from('consignments').select('*').eq('id', id).maybeSingle(),
      supabase.from('consignment_items').select('*').eq('consignment_id', id),
      supabase
        .from('consignment_payments')
        .select('*')
        .eq('consignment_id', id)
        .order('paid_at', { ascending: false, nullsFirst: false }),
      supabase.from('distributors').select('id, business_name'),
      supabase.from('product_variants').select('*'),
      supabase.from('products').select('id, name'),
      supabase.from('sizes').select('*'),
    ]);

  if (!consRes.data) return null;

  const cons = consRes.data as Record<string, any>;
  const distributorMap = new Map(
    ((distributorsRes.data ?? []) as Array<Record<string, any>>).map((d) => [d.id, d.business_name]),
  );
  const variantMap = new Map(
    ((variantsRes.data ?? []) as Array<Record<string, any>>).map((v) => [v.id, v]),
  );
  const productMap = new Map(
    ((productsRes.data ?? []) as Array<Record<string, any>>).map((p) => [p.id, p.name]),
  );
  const sizeMap = new Map(
    ((sizesRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s.name]),
  );

  const items: ConsignmentItemDetail[] = ((itemsRes.data ?? []) as Array<Record<string, any>>).map(
    (it) => {
      const variant = variantMap.get(it.variant_id);
      return {
        id: it.id,
        variantId: it.variant_id,
        productName: variant ? productMap.get(variant.product_id) ?? '' : '',
        sizeLabel: variant ? sizeMap.get(variant.size_id) ?? '' : '',
        quantityDelivered: it.quantity_delivered ?? 0,
        quantityReturned: it.quantity_returned ?? 0,
        quantitySold: it.quantity_sold ?? 0,
        unitPrice: Number(it.unit_price),
      };
    },
  );

  const payments: ConsignmentPayment[] = ((payRes.data ?? []) as Array<Record<string, any>>).map(
    (p) => ({
      id: p.id,
      consignmentId: p.consignment_id,
      amount: Number(p.amount),
      paidAt: p.paid_at ?? '',
      notes: p.notes ?? '',
      createdAt: p.created_at ?? '',
    }),
  );

  return {
    id: cons.id,
    distributorName: cons.distributor_id ? distributorMap.get(cons.distributor_id) ?? '' : '',
    status: cons.status ?? '',
    notes: cons.notes ?? '',
    createdAt: cons.created_at ?? '',
    items,
    payments,
  };
}

// Registra un abono (pago parcial o total) contra una consignación.
export async function addConsignmentPayment(input: {
  consignmentId: string;
  amount: number;
  paidAt: string;
  notes: string;
}): Promise<{ error: string | null }> {
  if (!(await canChangeStatus('consignments'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  const { error } = await supabase.from('consignment_payments').insert({
    consignment_id: input.consignmentId,
    amount: Number(input.amount),
    paid_at: input.paidAt || null,
    notes: input.notes.trim() || null,
  });
  if (error) return { error: error.message };

  recordAudit({
    entityType: 'consignments',
    entityId: input.consignmentId,
    action: 'UPDATE',
    fieldName: 'payment',
    newValue: `$${input.amount}`,
  });
  await recalcForConsignment(input.consignmentId);
  return { error: null };
}

// Elimina un abono registrado.
export async function deleteConsignmentPayment(
  id: string,
  consignmentId: string,
): Promise<{ error: string | null }> {
  if (!(await canWrite('consignments'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  const { error } = await supabase.from('consignment_payments').delete().eq('id', id);
  if (error) return { error: error.message };

  recordAudit({
    entityType: 'consignments',
    entityId: consignmentId,
    action: 'UPDATE',
    fieldName: 'payment',
    oldValue: 'deleted',
    newValue: '',
  });
  await recalcForConsignment(consignmentId);
  return { error: null };
}

// Actualiza las cantidades devueltas/vendidas de un ítem de consignación.
// Si aumenta lo devuelto, reingresa la mercancía al inventario disponible.
export async function updateConsignmentItem(
  itemId: string,
  consignmentId: string,
  patch: { quantityReturned: number; quantitySold: number },
): Promise<{ error: string | null }> {
  if (!(await canWrite('consignments'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  const { data: current } = await supabase
    .from('consignment_items')
    .select('variant_id, quantity_returned')
    .eq('id', itemId)
    .maybeSingle();
  const cur = (current ?? {}) as Record<string, any>;
  const prevReturned = Number(cur.quantity_returned ?? 0);
  const delta = patch.quantityReturned - prevReturned;

  const { error } = await supabase
    .from('consignment_items')
    .update({ quantity_returned: patch.quantityReturned, quantity_sold: patch.quantitySold })
    .eq('id', itemId);
  if (error) return { error: error.message };

  if (delta > 0 && cur.variant_id) {
    const { data: inv } = await supabase
      .from('inventory')
      .select('available, consigned')
      .eq('variant_id', cur.variant_id)
      .maybeSingle();
    const available = Number(inv?.available ?? 0);
    const consigned = Number(inv?.consigned ?? 0);
    const nextAvailable = available + delta;
    const nextConsigned = Math.max(0, consigned - delta);

    await supabase
      .from('inventory')
      .update({ available: nextAvailable, consigned: nextConsigned })
      .eq('variant_id', cur.variant_id);

    await supabase.from('inventory_movements').insert({
      variant_id: cur.variant_id,
      movement_type: 'CONSIGNMENT_RETURN',
      quantity: delta,
      prev_available: available,
      new_available: nextAvailable,
      prev_consigned: consigned,
      new_consigned: nextConsigned,
      reason: 'Retorno de mercancía en consignación',
    });
  }

  recordAudit({
    entityType: 'consignments',
    entityId: consignmentId,
    action: 'UPDATE',
    fieldName: 'item',
    newValue: `devueltos: ${patch.quantityReturned}, vendidos: ${patch.quantitySold}`,
  });
  await recalcForConsignment(consignmentId);
  return { error: null };
}

// Lista las consignaciones de un distribuidor (para el desplegable por distribuidor).
export interface DistributorConsignmentSummary {
  id: string;
  status: string;
  itemCount: number;
  createdAt: string;
  notes: string;
}

export async function fetchDistributorConsignments(
  distributorId: string,
): Promise<DistributorConsignmentSummary[]> {
  if (!isSupabaseConfigured || !supabase) return [];

  const [consRes, itemsRes] = await Promise.all([
    supabase.from('consignments').select('*').eq('distributor_id', distributorId),
    supabase.from('consignment_items').select('consignment_id, quantity_delivered'),
  ]);

  const countMap = new Map<string, number>();
  ((itemsRes.data ?? []) as Array<Record<string, any>>).forEach((it) => {
    countMap.set(
      it.consignment_id,
      (countMap.get(it.consignment_id) ?? 0) + Number(it.quantity_delivered),
    );
  });

  return ((consRes.data ?? []) as Array<Record<string, any>>)
    .map((c) => ({
      id: c.id,
      status: c.status ?? '',
      itemCount: countMap.get(c.id) ?? 0,
      createdAt: c.created_at ?? '',
      notes: c.notes ?? '',
    }))
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
}

// Recalcula el estado de crédito de un distribuidor a partir de su saldo
// abierto en consignación:
//   - sin saldo pendiente            -> "Al día"
//   - con saldo y sin abonos         -> "Pendiente de pago"
//   - con saldo y con abonos         -> "Pago parcial"
// La mercancía en consignación (entregada y no devuelta) mantiene el estado
// en pendiente/parcial; solo al pagar o devolver todo pasa a "Al día".
export async function recalculateDistributorCreditStatus(
  distributorId: string,
): Promise<{ error: string | null }> {
  if (!isSupabaseConfigured || !supabase) return { error: null };

  try {
    const { data: consignments } = await supabase
      .from('consignments')
      .select('id')
      .eq('distributor_id', distributorId);

    const ids = ((consignments ?? []) as Array<{ id: string }>).map((c) => c.id);

    if (ids.length === 0) {
      const { error } = await supabase
        .from('distributors')
        .update({ credit_status: 'Al día' })
        .eq('id', distributorId);
      return { error: error?.message ?? null };
    }

    const [itemsRes, paymentsRes] = await Promise.all([
      supabase
        .from('consignment_items')
        .select('quantity_delivered, quantity_returned, unit_price')
        .in('consignment_id', ids),
      supabase
        .from('consignment_payments')
        .select('amount')
        .in('consignment_id', ids),
    ]);

    let totalToPay = 0;
    for (const it of (itemsRes.data ?? []) as Array<Record<string, any>>) {
      const delivered = Number(it.quantity_delivered ?? 0);
      const returned = Number(it.quantity_returned ?? 0);
      const unit = Number(it.unit_price ?? 0);
      totalToPay += Math.max(0, delivered - returned) * unit;
    }

    const totalPaid = ((paymentsRes.data ?? []) as Array<Record<string, any>>).reduce(
      (sum, p) => sum + Number(p.amount ?? 0),
      0,
    );

    const remaining = totalToPay - totalPaid;
    let status: string;
    if (remaining <= 0) status = 'Al día';
    else if (totalPaid <= 0) status = 'Pendiente de pago';
    else status = 'Pago parcial';

    const { error } = await supabase
      .from('distributors')
      .update({ credit_status: status })
      .eq('id', distributorId);

    return { error: error?.message ?? null };
  } catch (err) {
    console.warn('[consignments] credit status recalc failed:', err);
    return { error: 'Error al recalcular el estado de crédito.' };
  }
}

// Recalcula el estado de crédito del distribuidor asociado a una consignación.
async function recalcForConsignment(consignmentId: string): Promise<void> {
  const { data: cons } = await supabase
    .from('consignments')
    .select('distributor_id')
    .eq('id', consignmentId)
    .maybeSingle();
  const distributorId = (cons as Record<string, any> | null)?.distributor_id;
  if (distributorId) await recalculateDistributorCreditStatus(distributorId);
}